import { ovl, SYSForm, SYSFormValidationValidators } from "../../"
import { getDateISOString, getDateValue } from "../../global/globals"
import { HookLookupsRefresh, HookLookupsRefresh_Type } from "../../global/hooks"
import { _GetFieldList, getDisplayValue, getListValue } from "../table/helpers"
import { TextEnsureNewLine } from "../../textapi"
import type { ListDefinition } from "../table/table"
import type { SYSField, SYSFormState, ValidateFieldType } from "./actions"
import { _ValidateField } from "./actions"
import { FormGet, IsFieldValidOptions } from "./formapi"

import {
  GetRowFromFormState,
  KeyValueListFromServerFn,
} from "./controls/helpers"

import {
  _clearFieldList,
  _setFieldInactive,
  _TryGetLookupFromServerInFormInitIfNotFound,
  sortedReplacer,
} from "./helper"

import { FocusSet } from "../../global/globalapi"
import { _setFormValid, setVisibleValidation } from "./validators"

export type SetFieldStateMode =
  | "visible/notvisible"
  | "readonly/notvisible"
  | "visible/readonly"
export type FieldSetStateOptionsType = {
  animate?: "fade" | "none"
  mode?: SetFieldStateMode
  focusWhenVisible?: boolean
  focusForce?: boolean
  selectIfFocusSet?: boolean
  forceValueWhenInactive?: any
  forceValue?: any
  forceWatched?: boolean
  keepValidation?: boolean
  clearList?: boolean
  noRetrigger?: boolean
}

export type SYSFields = { [key in string]: SYSField }

export const FieldValidate = async (
  field: SYSField,
  ignoreCustomValidation = false
) => {
  if (!field?._state || field._state.removedAndIrrelevantInCurrentSituation) {
    return
  }
  if (field.list) {
    field.value = field.convertedValue
  }
  let formState = FormGet(<SYSForm>field.formType, {
    instance: field.formId,
  }).formState
  _ValidateField(
    field,
    formState,
    ovl.getActions(),
    undefined,
    ignoreCustomValidation,
    undefined
  )
  //setVisibleValidation(formState)
}

export const FieldInitMandatory = async (
  field: SYSField,
  mandatoryValidator: SYSFormValidationValidators
) => {
  // allow user to set a field mandatory by config in forminit
  // restricted to init process by now because other usages need testing

  if (!field?._state || field._state.removedAndIrrelevantInCurrentSituation) {
    return
  }
  if (field._state.fieldInitialValidated) {
    throw Error(
      `SYS Error: FieldInitMandatory only in Init possible. Field: ${field.fieldKey}`
    )
  }
  field.schema.nullable = false
  field.schema.nullValidatorToUse = mandatoryValidator
}

export const FieldSetState = async (
  field: SYSField,
  active: boolean,
  options?: FieldSetStateOptionsType
) => {
  if (!field?._state || field._state.removedAndIrrelevantInCurrentSituation) {
    return
  }
  // let valueToSetWhenInactive
  // let valueToSet
  // if (options) {
  //   if (options.hasOwnProperty("forceValueWhenInactive")) {
  //     valueToSetWhenInactive = { val: options.forceValueWhenInactive }
  //   }
  //   if (options.hasOwnProperty("forceValue")) {
  //     valueToSet = { val: options.forceValue }
  //   }
  // }
  await _setFieldInactive(
    field,
    !active,
    options?.animate,
    options?.mode,
    options?.focusWhenVisible,
    false,
    options?.forceValueWhenInactive,
    options?.forceValue,
    options?.keepValidation,
    options?.clearList,
    options?.focusForce,
    options?.selectIfFocusSet,
    options?.noRetrigger
  )
}

export type SetFieldValueOptions = {
  debounce?: number
  /* todo try to get rid of those 
     and use isCorrected instead?
  */
  ignoreCustomValidation?: boolean
  ignoreValueChanged?: boolean
  /* -- */

  watched?: boolean
  forceChanged?: boolean
  clearList?: boolean
  setFocus?: boolean
  userTriggered?: boolean
}
type _FieldForceChange = {
  watched?: boolean
  setFocus?: boolean
}
export const FieldForceChange = async (
  field: SYSField,
  options?: _FieldForceChange
) => {
  if (!field?._state) {
    return
  }
  field._state.valueChangedHandled = true
  FieldSetVal(field, FieldVal(field), {
    forceChanged: true,
    watched: options?.watched,
    setFocus: options?.setFocus,
  })
}

type FieldSetForceChangeOptions = {
  setFocus?: boolean
}

export const FieldSetForceChange = async (
  field: SYSField,
  options?: FieldSetForceChangeOptions
) => {
  if (!field?._state) {
    return
  }

  ovl.getActions().ovl.internal._ValidateDataType({
    field,
    formState: ovl.getState().ovl.forms[field.formType][field.formId],
    isInnerEvent: false,
  } as ValidateFieldType)

  field._state.forceChange = true
  field._state.valueChangedHandled = true

  if (options?.setFocus) {
    FieldSetFocus(field)
  }
}
let fieldSetValDebounce = {}
export const FieldSetVal = async (
  field: SYSField,
  value: any,
  options?: SetFieldValueOptions
) => {
  if (!field?._state || field._state.removedAndIrrelevantInCurrentSituation) {
    // don't do anything if field is irrelevant
    return
  }

  if (options?.userTriggered === false) {
    field._state.noRetrigger = false
  }

  if (options?.debounce !== undefined) {
    // we need to remember if we were in init mode when calling setFieldVal debounced
    let formState: SYSFormState =
      ovl.getState().ovl.forms[field.formType][field.formId]
    if (!formState) {
      return
    }
    let wasInit = formState.firstInitOrSituationChanged

    if (fieldSetValDebounce[field.fieldKey] !== undefined) {
      clearTimeout(fieldSetValDebounce[field.fieldKey])
    }
    fieldSetValDebounce[field.fieldKey] = setTimeout(async () => {
      try {
        formState._state._fieldSetValFromDebouncedInit = wasInit
        await _fieldSetVal(field, value, options)
      } finally {
        formState._state._fieldSetValFromDebouncedInit = false
      }
    }, options.debounce)
  } else {
    await _fieldSetVal(field, value, options)
  }
}

const _fieldSetVal = async (
  field: SYSField,
  value: any,
  options?: SetFieldValueOptions
) => {
  let formState: SYSFormState =
    ovl.getState().ovl.forms[field.formType][field.formId]
  if (!formState) {
    return
  }
  if (value === undefined) {
    value = null
  }

  // 20250526 knp, focusout on textbox checks value when it got focus with value when it lost focus
  // this won't fire if we set the value of the current field using this function because its not recognised as a change
  // so also set the remembered value in this case
  // i also did that in value.setCorrectedValue
  if (value !== null) {
    field._state.valueAfterGotFocus = value.toString()
  } else {
    field._state.valueAfterGotFocus = null
  }
  if (
    field.list &&
    !options?.userTriggered &&
    _TryGetLookupFromServerInFormInitIfNotFound(field, value)
  ) {
    await FieldList(field, {
      forceNoSnack: true,
      checkOnServerValue: value,
    })
  }
  if (value && field.type === "date" && typeof value.getMonth === "function") {
    // its a date already
    value = getDateISOString(value)
    value = getDateValue(value)
  } else if (
    field.type === "datetime" &&
    value !== undefined &&
    value !== null &&
    value.length > 16
  ) {
    value = value.replace("+00:00", "Z")
    let dt = new Date(value)
    let t = dt.getTimezoneOffset()
    var offsetTime = new Date(dt.getTime() - t * 60 * 1000)
    value = offsetTime.toISOString().substring(0, 16)
  }
  if (options?.forceChanged) {
    field._state.forceChange = true
  }

  //field._state.valueChangedHandled = true
  await ovl.getActions().ovl.form._ChangeField({
    fieldKey: field.fieldKey,
    formState,
    value,
    watched: options?.watched,
    ignoreCustomValidation: options?.ignoreCustomValidation,
    ignoreValueChanged: options?.ignoreValueChanged,
    isInnerEvent: false,
    noRetrigger: options?.userTriggered,
  })
  if (options?.clearList) {
    _clearFieldList(field)
  }
  if (options?.setFocus) {
    FieldSetFocus(field)
  }
}

export const FieldIsValid = (
  field: SYSField,
  options?: { invalidIfRemoved: boolean }
) => {
  if (
    field === undefined ||
    !field._state.fieldInitialValidated ||
    (field._state.removedAndIrrelevantInCurrentSituation &&
      options?.invalidIfRemoved)
  ) {
    return false
  }
  // we need to check if we are within ChangeField resp. FormInit Process
  // because if yes the we have Hooks that get called in between (Before Setting Field.valid)
  // and therefore we should check the whole thing
  // i was thinking of setting field.valid before every hook call.
  // but imo that wouldn't work if we SetFieldValue of another field in that hook and so on
  // so its a safe bet to just use the full shabang as it was before introducing this .valid check
  // (remember .valid checking and therefore tracking only .valid makes perfectly sense for outside compss depending on field validity)
  let formState = <SYSFormState>(
    ovl.getState().ovl.forms[field.formType]?.[field.formId]
  )
  if (formState && formState._state._inInitOrChangedCount > 0) {
    return FieldIsValidSelective(field)
  }
  return field.valid
}

export const FieldIsValidSelective = (
  field: SYSField,
  options?: IsFieldValidOptions
) => {
  if (field === undefined || !field._state.fieldInitialValidated) {
    return false
  }
  if (field.inactive && !field.inactiveButRelevantForValidation) {
    return true
  }

  let allValidation = []
  if (!options?.ignoreSystemValidations) {
    allValidation = allValidation.concat(field.validationResult.System)
  }
  if (!options?.ignoreValidateValidations) {
    allValidation = allValidation.concat(field.validationResult.Validate)
  }

  if (!options?.ignoreValueChangedValidations) {
    allValidation = allValidation.concat(field.validationResult.ValueChanged)
  }
  if (options?.ignoreValidationKeys) {
    allValidation = allValidation.filter(
      (f) => options?.ignoreValidationKeys.indexOf(f.translationKey) < 0
    )
  }
  return allValidation.length === 0
}

export const FieldIsFocused = (field: SYSField) => {
  if (!field?._state || field._state.removedAndIrrelevantInCurrentSituation) {
    return false
  }
  return field.hasFocus
}

export const FieldIsVisible = (field: SYSField) => {
  if (!field?._state || field._state.removedAndIrrelevantInCurrentSituation) {
    return false
  }
  return field._state.visible
}

export const FieldIsTouched = (field: SYSField) => {
  if (!field?._state || field._state.removedAndIrrelevantInCurrentSituation) {
    return false
  }
  return field.touched
}

export const FieldIsVisibleOrFillsSpace = (field: SYSField) => {
  return field?.ui?.fillSpaceIfNotVisible || FieldIsVisible(field)
}

export const FieldIsDirty = (field: SYSField) => {
  if (
    !field?._state ||
    field._state.removedAndIrrelevantInCurrentSituation ||
    field.schema.isCalculatedColumn === true ||
    (field.inactive && !field.schema.isRelevantForDirtyIfInactive)
  ) {
    return false
  }

  let convertedValue = field.convertedValue
  let originalValue = field.originalValue

  if (field.type === "object") {
    // we need a sortedreplacer because elsewise we cannot compare because the order of props is different
    convertedValue = JSON.stringify(convertedValue, sortedReplacer)
    originalValue = JSON.stringify(originalValue, sortedReplacer)
  }
  if (field.ui?.hasLineBreaks) {
    //handle /r and /n as the same  (eg. sap business one converts \n to \r on its own and then the field would differ)
    if (
      typeof originalValue === "string" &&
      typeof convertedValue === "string"
    ) {
      return (
        TextEnsureNewLine(originalValue) !== TextEnsureNewLine(convertedValue)
      )
    }
  }
  return originalValue !== convertedValue
}

export const FieldIsNothing = (field: SYSField) => {
  if (field === undefined) {
    return true
  }
  return field.convertedValue === undefined || field.convertedValue === null
}

export const FieldIsEmpty = (field: SYSField) => {
  return FieldIsNothing(field) || field.convertedValue === ""
}

export const FieldIsActive = (field: SYSField) => {
  if (!field?._state || field._state.removedAndIrrelevantInCurrentSituation) {
    return false
  }
  return !field.inactive
}

export const FieldIsWatched = (field: SYSField) => {
  if (field === undefined) {
    return false
  }
  return field.watched
}

export interface FieldValueOptionsType {
  useDisplayValue?: boolean
  mode?: "RawCurrent" | "Converted"
  lookupColumnKey?: string
  ignoreValidationKeys?: string[]
}

export interface FieldByKeyValueOptionsType extends FieldValueOptionsType {
  instance?: string
}

export const FieldSetFocus = (field: SYSField, forceSelect?: boolean) => {
  if (!field?._state) {
    return
  }
  if (document.getElementById(field.id)) {
    // if we set focus from inside validate, changed hooks its important to first execute the handler
    // otherwise changed will be triggered from within input and so on... (discovered in TOTP login input boxes)
    setTimeout(() => {
      FocusSet(field.id, { forceSelect })
    }, 50)
  } else {
    // its not visible yet (maybe we are inside form Init?)
    // so just set the key so it gets focused when ready
    ;(<SYSFormState>(
      ovl.getState().ovl.forms[field.formType][field.formId]
    )).forceFocusWhenVisibleFieldKey = field.fieldKey
  }
}

export const FieldForceSave = (field: SYSField) => {
  if (!field?._state) {
    return
  }
  field._state.forceSave = true
}

export const FieldValByKey = (
  fieldKey: string,
  formType: SYSForm,
  options?: FieldByKeyValueOptionsType
): any => {
  let instance: string = formType
  if (options?.instance) {
    instance = options?.instance
  }
  let field = ovl.getState().ovl.forms[formType][instance].fields[fieldKey]
  return FieldVal(field, options)
}

// export const FieldValidate = (field: SYSField, formState: SYSFormState) => {
//   if (!field?._state) {
//     return
//   }
//   _ValidateField(field, formState, ovl.getActions(), false)
// }

export const FieldCurrentVal = (field: SYSField): any => {
  if (field === undefined) {
    return undefined
  }
  return field.value
}

export const FieldVal = (
  field: SYSField,
  options?: FieldValueOptionsType
): any => {
  if (field === undefined) {
    return undefined
  }
  let val
  let mode = options?.mode
  if (!mode) {
    // if (SYSConfig._system.fieldValConvertedValueIfInvalid) {
    //   // see flag for desc
    mode = "Converted"
    // } else {
    //   // somewhen in 2021: if going for the finished valid value but its not valid then return undefined
    //   // should help in validation to not need a IsFieldValid followed by a getVal
    //   // 2022-12-20: nopeeee -> this is the "old" style and will be depreceated âfter running new style successfully for a while
    //   let isValid = FieldIsValidSelective(field, {
    //     ignoreValidationKeys: options?.ignoreValidationKeys,
    //   })
    //   if (!isValid) {
    //     return undefined
    //   }
    //}
    val = field.convertedValue
  } else if (mode === "Converted") {
    // always get converted value when request like this
    val = field.convertedValue
  } else if (mode === "RawCurrent") {
    // always get raw current value when request like this
    val = field.value
  }
  let useDisplayValue = options?.useDisplayValue
  let formState: SYSFormState =
    ovl.getState().ovl.forms[field.formType][field.formId]
  let lookupColumnKey = options?.lookupColumnKey
  if (useDisplayValue) {
    return getDisplayValue(
      field.fieldKey,
      { list: field.list, type: field.type, ui: field.ui },
      val,
      formState.feature,
      formState,
      formState.tableDefId,
      lookupColumnKey
    )
  } else {
    if (!options?.lookupColumnKey || !field.list) {
      return val
    }
    return getListValue(
      field.fieldKey,
      val,
      formState.feature,
      field.list,
      formState,
      lookupColumnKey
    )
  }
}
export type FieldListModeType = "Refresh" | "ClearAndRefresh" | "LocalRefresh"
export type FieldListOptionsType = {
  mode?: FieldListModeType
  filterValue?: string
  checkOnServerValue?: any
  forceNoSnack?: boolean
  notFullList?: boolean
}

export const FieldCurrentList = (field: SYSField): ListDefinition => {
  if (!field?._state || !field.list) {
    return
  }
  let formState: SYSFormState =
    ovl.getState().ovl.forms[field.formType][field.formId]
  // its a local list only
  return _GetFieldList(
    undefined,
    field.fieldKey,
    formState,
    formState?.tableDefId,
    true,
    false
  )
}

export const FieldList = async (
  field: SYSField,

  options?: FieldListOptionsType
): Promise<ListDefinition> => {
  if (!field?._state || !field.list) {
    return
  }
  let functionName = HookLookupsRefresh
  let formState: SYSFormState =
    ovl.getState().ovl.forms[field.formType][field.formId]
  let fn = ovl.getActions().app.features[formState.feature]
  if (!fn.hooks[functionName] || !field.list.serverEndpoint) {
    // its a local list only
    let res = _GetFieldList(
      undefined,
      field.fieldKey,
      formState,
      formState?.tableDefId,
      true,
      options?.notFullList === true
    )
    //_setFieldWatched(field, false)
    await FieldValidate(field, undefined)
    //    if (options?.validateField) {
    // await ovl.getActions().ovl.internal._ValidateList({
    //   field,
    //   correctedValueToSet: undefined,
    //   formState,
    //   isInnerEvent: false,
    //   value: field.convertedValue,
    //   listData: res,
    // })
    //}

    return res
  } else {
    let listDataState = fn.hooks[functionName](<HookLookupsRefresh_Type>{
      formState,
      tableDefId: formState.tableDefId,
    })
    if (!listDataState) {
      return
    }
    if (
      options?.mode !== "LocalRefresh" // &&
      // (options.notFullList !== true ||
      //   field.list.tryGetLookupFromServerInFormInitIfNotFoundIgnoreValues ===
      //     "None" ||
      //   field.list.tryGetLookupFromServerInFormInitIfNotFoundIgnoreValues.indexOf(
      //     options.filterValue
      //   ) < 0)
    ) {
      let forceClear = false
      if (options?.mode === "ClearAndRefresh") {
        forceClear = true
      }

      if (options?.checkOnServerValue !== undefined) {
        options.filterValue = options.checkOnServerValue
      }
      await KeyValueListFromServerFn(
        ovl.getState(),
        field.list,
        field.fieldKey,
        listDataState,
        options?.filterValue,
        GetRowFromFormState(
          formState,
          options?.checkOnServerValue !== undefined
            ? field.fieldKey
            : undefined,
          options?.checkOnServerValue !== undefined
            ? options.checkOnServerValue
            : undefined
        ),
        formState.feature,
        formState.subFeature,
        formState.formType,
        formState.formId,
        false,
        options?.forceNoSnack,
        forceClear,
        options?.notFullList || options?.checkOnServerValue !== undefined
      )
    }
    let res = _GetFieldList(
      undefined,
      field.fieldKey,
      formState,
      formState?.tableDefId,
      true
    )
    //_setFieldWatched(field, false)
    await FieldValidate(field, undefined)
    // await ovl.getActions().ovl.internal._ValidateList({
    //   field,
    //   correctedValueToSet: undefined,
    //   formState,
    //   isInnerEvent: false,
    //   value: field.convertedValue,
    //   listData: res,
    // })

    // this is necessary because we have in BAR ACTActivities
    // await FieldSetVal(formState.fields.CardCode, state.ovl.user.cardCode)
    // -> at this stage the form IsInvalid because the cardcode is not in the list because the list is empty in that case
    // // we also need the List... Because we do not manually trigger it
    // await FieldList(formState.fields.CardCode, { notFullList: true })
    // -> now we expect the error from before to be gone. thats why we need the _setFormValid()
    // console.log("ScreenNavigaeInFinished formvalid:", FormIsValid(formState))
    _setFormValid(formState)
    return res
  }
}
export type { SYSField, SYSFormState } from "../../library/forms/actions"
export type { SYSFormField } from "../../library/forms/sysformelement"
export type { ActiveInactiveVisiblity as SYSFieldVisiblity } from "./actions"
